import * as React from 'react'
import {ForwardedRef, forwardRef, memo} from 'react'
import {Util} from 'yougile-util'
import {Backend} from '@common/backend'
import {createErrorBoundary} from '@containers/error-boundary/error-boundary'
import {cn} from '@containers/html-utils'

export interface ChatListNewProps<P extends {chatId: string}> {
  chats: ReadonlyArray<P>
  chatView: React.ComponentType<P>
  itemClick: (chatId: string) => void
  itemHover: (idx: number) => void
  selectedIx: number
  from: number
  to: number
  itemHeight: number
}

const Item = memo(
  <P extends {chatId: string}>({
    item,
    chatView: ChatView
  }: {
    item: P
    chatView: React.ComponentType<P>
  }) => <ChatView {...item} />
)

const ItemWithErrorBoundary = createErrorBoundary<{
  error: Error
  errorInfo: React.ErrorInfo
  item: {chatId: string}
  chatView: React.ComponentType<{chatId: string}>
}>(({error, ...props}) => {
  React.useEffect(() => {
    if (error) {
      void Backend.report(
        'chat-list-item-crash',
        JSON.stringify({
          error,
          id: props.item.chatId
        })
      )
    }
  }, [String(error)])
  if (error) {
    return <></>
  }
  return <Item {...props} />
})

export const ChatListNew = forwardRef(
  <P extends {chatId: string}>(
    props: ChatListNewProps<P>,
    ref: ForwardedRef<HTMLUListElement>
  ) => {
    const {chats, selectedIx, itemHover, itemClick} = props
    const {chatView, itemHeight} = props
    const from = Math.max(0, props.from)
    const to = Math.min(Number.MAX_SAFE_INTEGER, props.to)
    const bem = Util.bemFrom('chat-list')

    return (
      <ul
        ref={ref}
        className={cn(bem({new: true}), 'relative')}
        style={{height: `${chats.length * itemHeight}px`}}
      >
        {chats.slice(from, to).map((item, i) => {
          const ix = from + i
          return (
            <li
              className={bem('item', {selected: selectedIx === ix})}
              onClick={() => itemClick?.(item.chatId)}
              onMouseOver={() => itemHover?.(ix)}
              key={item.chatId + '-' + ix}
              style={{
                position: 'absolute',
                left: 0,
                transform: `translateY(${ix * itemHeight}px)`,
                width: '100%',
                height: `${itemHeight}px`
              }}
            >
              <ItemWithErrorBoundary item={item} chatView={chatView as any} />
            </li>
          )
        })}
      </ul>
    )
  }
)
